import React from 'react'
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material'
import type { CarBrand } from '../types'

type BrandSelectorProps = {
  options: Array<CarBrand>
  value: string
  loading: boolean
  onChange: (value: string) => void
}

const BrandSelector = ({ options, value, loading, onChange }: BrandSelectorProps) => {
  const helperId = React.useId()

  const selectedBrand = React.useMemo(
    () => options.find((brand) => brand.id === value) ?? null,
    [options, value],
  )

  return (
    <Box sx={{ width: '100%', maxWidth: 420 }}>
      <Autocomplete<CarBrand, false, false, false>
        disablePortal
        options={options}
        value={selectedBrand}
        loading={loading}
        onChange={(event, newValue) => onChange(newValue?.id ?? '')}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        getOptionLabel={(option) => option.name}
        noOptionsText={loading ? 'Chargement...' : 'Aucune marque'}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Marque (optionnel)"
            size="small"
            helperText="Jouez avec des véhicules d'une seule marque"
            FormHelperTextProps={{ id: helperId, sx: { color: 'white' } }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              fontFamily: 'Montserrat',

              '& .MuiOutlinedInput-root': {
                background: 'rgba(0,0,0,0.25)',
                borderRadius: 1,

                // texte saisi
                '& .MuiInputBase-input': {
                  color: 'white',
                },

                // bordure normale
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.25)',
                },

                // hover
                '&:hover fieldset': {
                  borderColor: 'rgba(255,255,255,0.55)',
                },

                // focus (plus de rouge)
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
              },

              // label
              '& .MuiInputLabel-root': {
                color: 'rgba(255,255,255,0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'white',
              },

              // croix reset + chevron dropdown + icônes
              '& .MuiSvgIcon-root': {
                color: 'white',
              },

              // ciblage précis croix reset (optionnel mais safe)
              '& .MuiAutocomplete-clearIndicator .MuiSvgIcon-root': {
                color: 'white',
              },
            }}
          />
        )}
      />
    </Box>
  )
}

export default BrandSelector